/**
 * Context Enricher
 *
 * Responsible for gathering and enriching context information for user actions.
 * Integrates with existing stores and services to provide comprehensive context.
 */

import { DeviceType } from '../types';
import { IdGenerator } from './IdGenerator';

import type {
  ActionContext,
  BusinessContext,
  ContextEnricher as IContextEnricher,
  MonitoringContextProvider,
  TechnicalContext,
  UserContext,
} from '../types';

// Type definitions for the Network Information API
interface NetworkInformation {
  effectiveType?: '2g' | '3g' | '4g' | 'slow-2g';
  type?:
    | 'bluetooth'
    | 'cellular'
    | 'ethernet'
    | 'none'
    | 'wifi'
    | 'wimax'
    | 'other'
    | 'unknown';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

/**
 * Context enricher implementation
 */
export class ContextEnricher implements IContextEnricher {
  private static instance: ContextEnricher;
  private contextProvider: MonitoringContextProvider | undefined;
  private idGenerator: IdGenerator;

  constructor(contextProvider?: MonitoringContextProvider) {
    this.contextProvider = contextProvider;
    this.idGenerator = IdGenerator.getInstance();
  }

  static getInstance(
    contextProvider?: MonitoringContextProvider,
  ): ContextEnricher {
    if (!ContextEnricher.instance) {
      ContextEnricher.instance = new ContextEnricher(contextProvider);
    }
    if (contextProvider && !ContextEnricher.instance.contextProvider) {
      ContextEnricher.instance.contextProvider = contextProvider;
    }
    return ContextEnricher.instance;
  }

  /**
   * Enrich action context with current application state
   */
  enrichContext(partialContext?: Partial<ActionContext>): ActionContext {
    const baseContext: ActionContext = {
      user: this.getUserContext(),
      technical: this.getTechnicalContext(),
      business: this.getBusinessContext(),
    };

    // Merge with any provided partial context
    if (partialContext) {
      return {
        user: { ...baseContext.user, ...partialContext.user },
        technical: { ...baseContext.technical, ...partialContext.technical },
        business: { ...baseContext.business, ...partialContext.business },
      };
    }

    return baseContext;
  }

  /**
   * Get current user context from context provider
   */
  getUserContext(): UserContext {
    try {
      const contextData = this.contextProvider?.();
      const userContext = contextData?.user;

      if (!userContext?.id) {
        return this.getAnonymousUserContext();
      }

      return {
        userId: userContext.id || 'anonymous',
        email: userContext.email || '',
        name: userContext.name || '',
        role: userContext.role || 'user',
        type: userContext.type || 'unknown',
        source: userContext.source || 'unknown',
        organizationId: contextData?.organization?.id,
        sessionId: this.getSessionId(),
      };
    } catch (error) {
      console.warn('Failed to get user context:', error);
      return this.getAnonymousUserContext();
    }
  }

  /**
   * Get current technical context
   */
  getTechnicalContext(): TechnicalContext {
    try {
      return {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        url: window.location.href,
        referrer: document.referrer || undefined,
        connectionType: this.getConnectionType(),
        deviceType: this.detectDeviceType(),
        browser: this.getBrowserInfo(),
        timing: this.getPerformanceTiming(),
      };
    } catch (error) {
      console.warn('Failed to get technical context:', error);
      return this.getFallbackTechnicalContext();
    }
  }

  /**
   * Get current business context from context provider
   */
  getBusinessContext(): BusinessContext {
    try {
      const contextData = this.contextProvider?.();

      return {
        storyId: contextData?.video?.story?.id,
        videoId: contextData?.video?.currentVideo?.id,
        workflowStage: this.getWorkflowStage(),
        featureFlags: this.getFeatureFlags(),
        experimentVariants: this.getExperimentVariants(),
        organization: contextData?.organization
          ? {
              id: contextData.organization.id,
              name:
                contextData.organization.title ||
                contextData.organization.name ||
                'Unknown Organization',
              slug: contextData.organization.slug,
            }
          : undefined,
        environment: this.getEnvironment(),
      };
    } catch (error) {
      console.warn('Failed to get business context:', error);
      return this.getFallbackBusinessContext();
    }
  }

  /**
   * Get anonymous user context for unauthenticated users
   */
  private getAnonymousUserContext(): UserContext {
    return {
      userId: 'anonymous',
      email: '',
      name: '',
      role: 'anonymous',
      type: 'anonymous',
      source: 'unknown',
      organizationId: undefined,
      sessionId: this.getSessionId(),
    };
  }

  /**
   * Get organization ID from current context
   */
  private getOrganizationId(): string | undefined {
    try {
      const contextData = this.contextProvider?.();
      return contextData?.organization?.id;
    } catch {
      return undefined;
    }
  }

  /**
   * Get or generate session ID
   */
  private getSessionId(): string {
    try {
      // Try to get from sessionStorage first
      let sessionId = sessionStorage.getItem('monitoring_session_id');

      if (!sessionId) {
        sessionId = this.idGenerator.generateSessionId();
        sessionStorage.setItem('monitoring_session_id', sessionId);
      }

      return sessionId;
    } catch {
      // Fallback if sessionStorage is not available
      return this.idGenerator.generateSessionId();
    }
  }

  /**
   * Detect device type based on user agent and viewport
   */
  private detectDeviceType(): DeviceType {
    const userAgent = navigator.userAgent.toLowerCase();
    const width = window.innerWidth;

    // Mobile detection
    if (
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      )
    ) {
      return width < 768 ? DeviceType.MOBILE : DeviceType.TABLET;
    }

    // Tablet detection based on viewport
    if (width >= 768 && width <= 1024) {
      return DeviceType.TABLET;
    }

    return DeviceType.DESKTOP;
  }

  /**
   * Get network connection type
   */
  private getConnectionType(): string | undefined {
    try {
      // Use type-safe access to the Network Information API
      const navigatorWithConnection = navigator as NavigatorWithConnection;
      const connection =
        navigatorWithConnection.connection ||
        navigatorWithConnection.mozConnection ||
        navigatorWithConnection.webkitConnection;
      return connection?.effectiveType || connection?.type;
    } catch {
      return undefined;
    }
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): { name: string; version: string } | undefined {
    try {
      const userAgent = navigator.userAgent;

      // Simple browser detection
      if (userAgent.includes('Chrome')) {
        const match = userAgent.match(/Chrome\/(\d+)/);
        return { name: 'Chrome', version: match?.[1] || 'unknown' };
      } else if (userAgent.includes('Firefox')) {
        const match = userAgent.match(/Firefox\/(\d+)/);
        return { name: 'Firefox', version: match?.[1] || 'unknown' };
      } else if (
        userAgent.includes('Safari') &&
        !userAgent.includes('Chrome')
      ) {
        const match = userAgent.match(/Version\/(\d+)/);
        return { name: 'Safari', version: match?.[1] || 'unknown' };
      } else if (userAgent.includes('Edge')) {
        const match = userAgent.match(/Edge\/(\d+)/);
        return { name: 'Edge', version: match?.[1] || 'unknown' };
      }

      return { name: 'Unknown', version: 'unknown' };
    } catch {
      return undefined;
    }
  }

  /**
   * Get performance timing information using modern Performance API
   */
  private getPerformanceTiming(): TechnicalContext['timing'] | undefined {
    try {
      // Use modern Performance API if available
      if ('getEntriesByType' in performance) {
        const navigationEntries = performance.getEntriesByType(
          'navigation',
        ) as PerformanceNavigationTiming[];
        if (navigationEntries.length > 0) {
          const entry = navigationEntries[0];
          return {
            navigationStart: entry.fetchStart,
            loadEventEnd: entry.loadEventEnd,
            domContentLoadedEventEnd: entry.domContentLoadedEventEnd,
          };
        }
      }

      // Fallback to deprecated API if modern API is not available
      if ('timing' in performance && (performance as any).timing) {
        const timing = (performance as any).timing;
        return {
          navigationStart: timing.navigationStart,
          loadEventEnd: timing.loadEventEnd,
          domContentLoadedEventEnd: timing.domContentLoadedEventEnd,
        };
      }

      return undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Get current workflow stage
   */
  private getWorkflowStage(): string | undefined {
    try {
      // Determine workflow stage based on current route or application state
      const path = window.location.pathname;

      if (path.includes('/dashboard')) return 'dashboard';
      if (path.includes('/editor')) return 'editing';
      if (path.includes('/upload')) return 'upload';
      if (path.includes('/share')) return 'sharing';

      return 'unknown';
    } catch {
      return undefined;
    }
  }

  /**
   * Get active feature flags
   */
  private getFeatureFlags(): Record<string, boolean> | undefined {
    // Integration with existing feature flag system
    // This would need to be adapted based on your actual feature flag implementation
    return {};
  }

  /**
   * Get experiment variants
   */
  private getExperimentVariants(): Record<string, string> | undefined {
    // Integration with A/B testing system if available
    return {};
  }

  /**
   * Get current environment
   */
  private getEnvironment(): string {
    return (
      process.env.DATO_ENV ||
      process.env.REACT_APP_DATO_ENV ||
      process.env.NODE_ENV ||
      'development'
    );
  }

  /**
   * Fallback technical context for error cases
   */
  private getFallbackTechnicalContext(): TechnicalContext {
    return {
      userAgent: 'unknown',
      viewport: { width: 0, height: 0 },
      url: 'unknown',
      deviceType: DeviceType.DESKTOP,
    };
  }

  /**
   * Fallback business context for error cases
   */
  private getFallbackBusinessContext(): BusinessContext {
    return {
      environment: 'unknown',
    };
  }
}
